import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import '../styles/EditGradeModal.css';
import {
    normalizeGradingMode,
    rowGradeToEditState,
} from '../utils/gradeValues';
import { extractErrorMessage } from '../utils/errorUtils';
import GradeFormatFields from './GradeFormatFields';

export default function EditGradeModal({ grade, onClose, onSave }) {
    // Determine grade type from id or grade_type
    const gradeId = grade.id || '';
    const isExam = gradeId.startsWith('exam_');
    const isAssignment = gradeId.startsWith('assignment_');
    const isActivity = gradeId.startsWith('activity_');

    const initialEdit = rowGradeToEditState(grade);

    const [formData, setFormData] = useState({
        grade_format: initialEdit.format,
        grade: initialEdit.value,
        grade_date: grade.grade_date || '',
        exam_type: grade.exam_type || '',
        exam_status: grade.exam_status || '',
        attempt: grade.attempt || 1,
        feedback: grade.feedback || '',
        activity_type: grade.activity_type || '',
        weight: grade.weight || 1.0,
        description: grade.description || ''
    });

    const [saveError, setSaveError] = useState('');

    useEffect(() => {
        const edit = rowGradeToEditState(grade);
        setSaveError('');
        setFormData({
            grade_format: edit.format,
            grade: edit.value,
            grade_date: grade.grade_date || '',
            exam_type: grade.exam_type || '',
            exam_status: grade.exam_status || '',
            attempt: grade.attempt || 1,
            feedback: grade.feedback || '',
            activity_type: grade.activity_type || '',
            weight: grade.weight || 1.0,
            description: grade.description || ''
        });
    }, [grade]);

    const examStatuses = [
        { value: 'PASSED', label: 'Zaliczony' },
        { value: 'FAILED', label: 'Niezaliczony' },
        { value: 'ABSENT', label: 'Nieobecny' },
        { value: 'EXEMPT', label: 'Zwolniony' }
    ];

    const examTypes = [
        { value: 'FINAL', label: 'Egzamin końcowy' },
        { value: 'MIDTERM', label: 'Egzamin śródsemestralny' },
        { value: 'RESIT', label: 'Egzamin poprawkowy' }
    ];

    const activityTypes = [
        { value: 'PRESENTATION', label: 'Prezentacja' },
        { value: 'QUIZ', label: 'Quiz' },
        { value: 'ACTIVITY', label: 'Aktywność' },
        { value: 'TEST', label: 'Test' },
        { value: 'OTHER', label: 'Inne' }
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'attempt' || name === 'weight' ? parseFloat(value) || 0 : value
        }));
    };

    const handleSave = () => {
        setSaveError('');
        const fmt = normalizeGradingMode(formData.grade_format);
        const hasGradeValue = formData.grade !== '' && formData.grade != null;

        if (isExam) {
            if (!formData.grade_date || !formData.exam_status) {
                setSaveError('Wypełnij datę i status egzaminu.');
                return;
            }
            if (!hasGradeValue && formData.exam_status !== 'ABSENT' && formData.exam_status !== 'EXEMPT') {
                setSaveError('Wybierz wynik lub ustaw status „Nieobecny” / „Zwolniony”.');
                return;
            }
        } else if (isActivity) {
            if (!hasGradeValue || !formData.activity_type) {
                setSaveError('Wypełnij ocenę i typ aktywności.');
                return;
            }
        } else if (!hasGradeValue || !formData.grade_date) {
            setSaveError('Wypełnij ocenę i datę.');
            return;
        }

        const updatedGrade = {
            ...grade,
            grade: hasGradeValue ? formData.grade : null,
            grade_format: fmt,
            grade_date: formData.grade_date,
        };

        if (isExam) {
            updatedGrade.exam_type = formData.exam_type;
            updatedGrade.exam_status = formData.exam_status;
            if (fmt === 'pass_fail' && hasGradeValue) {
                updatedGrade.exam_status = formData.grade === 'PASSED' ? 'PASSED' : 'FAILED';
            }
            updatedGrade.attempt = formData.attempt;
        } else if (isAssignment) {
            updatedGrade.feedback = formData.feedback;
        } else if (isActivity) {
            updatedGrade.activity_type = formData.activity_type;
            updatedGrade.weight = formData.weight;
            updatedGrade.description = formData.description;
        }

        onSave(updatedGrade);
    };

    return (
        <div className="egm-modal-backdrop" onClick={onClose}>
            <div className="egm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="egm-modal-header">
                    <h2>Edytuj ocenę</h2>
                    <button className="egm-close-btn" onClick={onClose} type="button">
                        <X size={20} />
                    </button>
                </div>

                <div className="egm-modal-body">
                    <div className="egm-form-group">
                        <label>Ocena / wynik</label>
                        <GradeFormatFields
                            className="egm-form-input"
                            format={formData.grade_format}
                            value={formData.grade}
                            onFormatChange={(next) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    grade_format: next,
                                    grade: normalizeGradingMode(next) === 'pass_fail' ? 'FAILED' : '',
                                }));
                            }}
                            onValueChange={(next) => setFormData((prev) => ({ ...prev, grade: next }))}
                        />
                    </div>

                    {saveError && (
                        <div className="form-error-banner" role="alert">
                            {extractErrorMessage(saveError)}
                        </div>
                    )}

                    {/* Date field */}
                    <div className="egm-form-group">
                        <label>
                            Data:
                            <input 
                                type="date" 
                                name="grade_date"
                                value={formData.grade_date} 
                                onChange={handleChange}
                                className="egm-form-input"
                            />
                        </label>
                    </div>

                    {/* Exam-specific fields */}
                    {isExam && (
                        <>
                            <div className="egm-form-group">
                                <label>
                                    Typ egzaminu:
                                    <select 
                                        name="exam_type"
                                        value={formData.exam_type} 
                                        onChange={handleChange}
                                        className="egm-form-input"
                                    >
                                        {examTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            <div className="egm-form-group">
                                <label>
                                    Status:
                                    <select 
                                        name="exam_status"
                                        value={formData.exam_status} 
                                        onChange={handleChange}
                                        className="egm-form-input"
                                    >
                                        {examStatuses.map(status => (
                                            <option key={status.value} value={status.value}>
                                                {status.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            <div className="egm-form-group">
                                <label>
                                    Próba:
                                    <input 
                                        type="number" 
                                        min="1" 
                                        name="attempt"
                                        value={formData.attempt} 
                                        onChange={handleChange}
                                        className="egm-form-input"
                                    />
                                </label>
                            </div>
                        </>
                    )}

                    {/* Assignment-specific fields */}
                    {isAssignment && (
                        <div className="egm-form-group">
                            <label>
                                Uwagi (feedback):
                                <textarea 
                                    name="feedback"
                                    value={formData.feedback} 
                                    onChange={handleChange}
                                    className="egm-form-input"
                                    rows="3"
                                />
                            </label>
                        </div>
                    )}

                    {/* Activity-specific fields */}
                    {isActivity && (
                        <>
                            <div className="egm-form-group">
                                <label>
                                    Typ aktywności:
                                    <select 
                                        name="activity_type"
                                        value={formData.activity_type} 
                                        onChange={handleChange}
                                        className="egm-form-input"
                                    >
                                        {activityTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            <div className="egm-form-group">
                                <label>
                                    Waga:
                                    <input 
                                        type="number" 
                                        step="0.1" 
                                        min="0.1" 
                                        name="weight"
                                        value={formData.weight} 
                                        onChange={handleChange}
                                        className="egm-form-input"
                                    />
                                </label>
                            </div>

                            <div className="egm-form-group">
                                <label>
                                    Opis:
                                    <textarea 
                                        name="description"
                                        value={formData.description} 
                                        onChange={handleChange}
                                        className="egm-form-input"
                                        rows="3"
                                    />
                                </label>
                            </div>
                        </>
                    )}

                </div>

                <div className="egm-modal-actions">
                    <button onClick={handleSave} className="btn btn-primary">Zapisz</button>
                    <button onClick={onClose} className="btn btn-secondary">Anuluj</button>
                </div>
            </div>
        </div>
    );
}